/**
 * Utilitários para formatação geral
 */

/**
 * Formata um número de telefone no padrão brasileiro (xx) xxxxx-xxxx
 * @param {string} value - Número a ser formatado
 * @returns {string} Número formatado
 */
export const formatWhatsApp = (value) => {
  if (!value) return '';
  // Limita para 11 dígitos e remove caracteres não numéricos
  value = value.replace(/\D/g, '').slice(0, 11);
  
  if (value.length <= 2) {
    return value.replace(/^(\d{0,2})/, '($1');
  }
  if (value.length <= 7) {
    return value.replace(/^(\d{2})(\d{0,5})/, '($1) $2');
  }
  return value.replace(/^(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
};

/**
 * Formata um número de telefone/celular
 * IMPORTANTE: Use esta função para toda formatação de números de telefone na aplicação
 * @param {string} value - Número a ser formatado
 * @returns {string} Número formatado
 */
export const formatPhone = (value) => {
  return formatWhatsApp(value);
};

/**
 * Formata um CPF no padrão brasileiro (000.000.000-00)
 * @param {string} value - CPF a ser formatado
 * @returns {string} CPF formatado
 */
export const formatCPF = (value) => {
  if (!value) return '';
  // Remove caracteres não numéricos e limita para 11 dígitos
  value = value.replace(/\D/g, '').slice(0, 11);
  
  if (value.length <= 3) {
    return value;
  }
  if (value.length <= 6) {
    return value.replace(/^(\d{3})(\d{0,3})/, '$1.$2');
  }
  if (value.length <= 9) {
    return value.replace(/^(\d{3})(\d{3})(\d{0,3})/, '$1.$2.$3');
  }
  return value.replace(/^(\d{3})(\d{3})(\d{3})(\d{0,2})/, '$1.$2.$3-$4');
};

/**
 * Formata um CNPJ no padrão brasileiro (00.000.000/0000-00)
 * @param {string} value - CNPJ a ser formatado
 * @returns {string} CNPJ formatado
 */
export const formatCNPJ = (value) => {
  if (!value) return '';
  // Remove caracteres não numéricos e limita para 14 dígitos
  value = value.replace(/\D/g, '').slice(0, 14);
  
  if (value.length <= 2) {
    return value;
  }
  if (value.length <= 5) {
    return value.replace(/^(\d{2})(\d{0,3})/, '$1.$2');
  }
  if (value.length <= 8) {
    return value.replace(/^(\d{2})(\d{3})(\d{0,3})/, '$1.$2.$3');
  }
  if (value.length <= 12) {
    return value.replace(/^(\d{2})(\d{3})(\d{3})(\d{0,4})/, '$1.$2.$3/$4');
  }
  return value.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, '$1.$2.$3/$4-$5');
};

/**
 * Formata um CEP no padrão brasileiro (00000-000)
 * @param {string} value - CEP a ser formatado
 * @returns {string} CEP formatado
 */
export const formatCEP = (value) => {
  if (!value) return '';
  
  // Remove caracteres não numéricos e limita para 8 dígitos
  const numericCep = value.replace(/\D/g, '').slice(0, 8);
  
  // Se o CEP estiver incompleto, retorna os números como estão
  if (numericCep.length <= 5) {
    return numericCep;
  }
  
  // Formata como 00000-000, garantindo que todos os 8 dígitos são mantidos
  return numericCep.replace(/^(\d{5})(\d{0,3})/, '$1-$2');
};

/**
 * Limpa caracteres não numéricos de uma string
 * @param {string} value - Valor a ser limpo
 * @returns {string} Valor apenas com números
 */
export const onlyNumbers = (value) => {
  if (!value) return '';
  return value.replace(/\D/g, '');
};

/**
 * Valida um email usando expressão regular
 * @param {string} email - Email a ser validado
 * @returns {boolean} True se o email for válido
 */
export const isValidEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Capitaliza a primeira letra de cada palavra
 * @param {string} text - Texto a ser formatado
 * @returns {string} Texto formatado
 */
export const capitalizeWords = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Trunca um texto para o tamanho máximo especificado
 * @param {string} text - Texto a ser truncado
 * @param {number} maxLength - Tamanho máximo
 * @param {string} suffix - Sufixo a ser adicionado ao final (padrão: "...")
 * @returns {string} Texto truncado
 */
export const truncateText = (text, maxLength, suffix = "...") => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + suffix;
};

/**
 * Formata nomes para exibição em espaços limitados
 * Opções mais simples para componentes que não precisam de formatação complexa
 * 
 * @param {string} name - Nome a ser formatado
 * @param {number} maxLength - Tamanho máximo (padrão: 15)
 * @returns {string} - Nome formatado
 */
export const formatNameForDisplay = (name, maxLength = 15) => {
  if (!name) return '';
  return truncateText(name, maxLength);
};

/**
 * Formata nomes para a legenda do calendário
 * Util para componentes de legenda com mais espaço
 * 
 * @param {string} name - Nome a ser formatado
 * @param {number} maxLength - Tamanho máximo (padrão: 18)
 * @returns {string} - Nome formatado
 */
export const formatNameForLegend = (name, maxLength = 18) => {
  if (!name) return '';
  return truncateText(name, maxLength);
};

/**
 * Formata nomes para dropdown ou tooltips
 * Útil para listas suspensas
 * 
 * @param {string} name - Nome a ser formatado
 * @param {number} maxLength - Tamanho máximo (padrão: 25)
 * @returns {string} - Nome formatado
 */
export const formatNameForDropdown = (name, maxLength = 25) => {
  if (!name) return '';
  return truncateText(name, maxLength);
};

/**
 * Formata nomes de profissionais para o cabeçalho do calendário
 * com estratégias diferentes baseadas no tamanho da tela
 * 
 * @param {string} name - Nome completo a ser formatado
 * @param {string} screenSize - Tamanho da tela ('xs', 'sm', 'md', 'lg')
 * @returns {string} - Nome formatado
 */
export const formatProfessionalNameForHeader = (name, screenSize = 'md') => {
  if (!name) return '';
  
  // Dividir o nome em partes
  const parts = name.split(' ');
  
  // Para telas muito pequenas (menores que 380px)
  if (screenSize === 'xs') {
    if (parts.length === 1) {
      // Nome único, mostrar no máximo 8 caracteres
      return truncateText(name, 7);
    } else {
      // Nome com sobrenome, mostrar apenas o primeiro nome (máximo 8 caracteres)
      const firstName = parts[0];
      return truncateText(firstName, 7);
    }
  } 
  // Para telas pequenas (menores que 500px)
  else if (screenSize === 'sm') {
    if (parts.length === 1) {
      // Nome único, mostrar no máximo 12 caracteres
      return truncateText(name, 11);
    } else if (parts.length === 2) {
      // Nome e sobrenome, mostrar primeiro nome + inicial do sobrenome
      const firstName = parts[0];
      const lastInitial = parts[1][0] || '';
      
      // Se primeiro nome for muito longo, truncar
      if (firstName.length > 10) {
        return `${truncateText(firstName, 9)} ${lastInitial}.`;
      }
      return `${firstName} ${lastInitial}.`;
    } else {
      // Múltiplas partes, mostrar primeiro nome + inicial do meio + inicial do último
      const firstName = parts[0];
      const middleInitial = parts.length > 2 ? parts[1][0] || '' : '';
      const lastInitial = parts[parts.length - 1][0] || '';
      
      // Se primeiro nome for muito longo, truncar
      if (firstName.length > 8) {
        return `${truncateText(firstName, 7)} ${middleInitial}. ${lastInitial}.`;
      }
      return `${firstName} ${middleInitial}. ${lastInitial}.`;
    }
  } 
  // Para telas médias e grandes, mostrar nome completo com truncamento se necessário
  else {
    // Tamanho máximo baseado no tipo de tela
    const maxLength = screenSize === 'md' ? 15 : 20;
    return truncateText(name, maxLength);
  }
}; 